import { CommerceMediaCollectionItem, FacetGroup, ProductPage } from '@api/types';
import { getPageContentType } from './content.utils';
import { getCommerceMediaByType } from './media.utils';

export enum ErpStatus {
  ACTIVE = '1', //Active
  NEW = '2', //Ny
  DISCONTINUED = '3', //Soppet
  TRANSITION = '4', //Overgang
  EXPIRED = '5', //Utgått
  ON_HOLD = '6', //På Vent
  NO_SALES_LAST_2_YEARS = '10', //Ingen oms siste 2 år
}

export const ERP_STATUSES = ErpStatus;

export const productHasDescription = (product: ProductPage) => product.productLongDescription;
export const productHasDocuments = (product: ProductPage) =>
  getCommerceMediaByType(product.commerceMediaCollection, 'Media').length > 0;
export const productHasSpecifications = (product: ProductPage) => {
  const specs = product?.specifications ?? [];
  return Boolean(Object.entries(specs).find((x) => x[1]?.find((y) => y.value)));
};
export const productHasAccessories = (product: ProductPage) =>
  product?.accessories && product?.accessories?.length > 0;
export const productHasSimilarProducts = (product: ProductPage) =>
  product?.relatedTo && product?.relatedTo?.length > 0;
export const productHasUpsaleProducts = (product: ProductPage) =>
  product?.upsale && product?.upsale?.length > 0;
export const productHasRelatedMaterials = (product: ProductPage) =>
  product?.pages && product?.pages?.length > 0;

// Product & Variant
//------------------

export const addParentProductData = <K extends keyof ProductPage>(
  variant: ProductPage,
  property: K
): ProductPage[K] extends Array<infer U> ? U[] : never => {
  const variantData = (variant[property] as unknown as any[]) || [];
  const parentData = (variant?.product?.expanded?.[property] as unknown as any[]) || [];
  return [...variantData, ...parentData] as ProductPage[K] extends Array<infer U> ? U[] : never;
};

export const getDetailsWithParentProductData = (productOrVariant: ProductPage): ProductPage => {
  const pageType = getPageContentType(productOrVariant);
  if (pageType === 'Product') {
    return productOrVariant;
  } else {
    const variant = productOrVariant;

    const variantCommerceMedia = variant.commerceMediaCollection || [];
    const productExpandedCommerceMedia = variant.product?.expanded?.commerceMediaCollection || [];

    const combinedCommerceMedia = [...variantCommerceMedia, ...productExpandedCommerceMedia];

    const uniqueCommerceMediaMap = new Map<string | number, CommerceMediaCollectionItem>(
      combinedCommerceMedia.map((item) => {
        const key = item.id ?? item.guidValue;
        return [key, item];
      })
    );

    const uniqueCommerceMediaCollection = Array.from(uniqueCommerceMediaMap.values());

    const mergedProduct: ProductPage = {
      ...variant.product?.expanded,
      itemERPDescription: variant.itemERPDescription,
      specifications: variant.specifications,
      grossPrice: variant.grossPrice,
      discountPrice: variant.discountPrice,
      code: variant.code,
      productNo: variant.productNo,
      stockQuantity: variant.stockQuantity,
      productInfoForm: variant.productInfoForm,
      productOrderForm: variant.productOrderForm,
      itemPackageBatteryCharger: variant.itemPackageBatteryCharger,
      itemPackageNumberBatteries: variant.itemPackageNumberBatteries,
      itemPackageNumberChargers: variant.itemPackageNumberChargers,
      commerceMediaCollection: uniqueCommerceMediaCollection,
    } as ProductPage;
    return mergedProduct;
  }
};

export const extractFacetKeysFromParams = (facetGroups?: FacetGroup[]) => {
  const facetKeys = [];
  const searchParams = new URLSearchParams(window.location.search);
  for (let param of searchParams) {
    const [paramKey, paramValue] = param;

    for (let filter of facetGroups || []) {
      if (filter.groupName === paramKey) {
        for (let facet of filter.facets || []) {
          if (facet.key && extractFilterValue(facet.key) === paramValue) {
            facetKeys.push(facet.key);
          }
        }
      }
    }
  }
  return facetKeys;
};

export function extractFilterValue(input?: string) {
  if (!input) {
    return '';
  }

  if (!input.includes('{')) {
    const filterValue = input.split(':')[1];
    return String(filterValue);
  }

  const jsonString = input?.split('{')[1].split('}')[0]; // Extracts the JSON part
  const jsonObject = JSON.parse('{' + jsonString + '}'); // Creates an object from the JSON string
  const value = extractValueFromArray(jsonObject.Value) || ''; // Returns the Value property of the object
  return String(value);
}

export const extractKeysFromURL = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const keys: { [key: string]: string } = {};
  searchParams.forEach((value, key) => {
    if (typeof key === 'string' && typeof value === 'string') {
      keys[key] = value;
    }
  });
  return keys;
};

export const extractValueFromArray = (input?: string) => {
  try {
    if (input) {
      let parsed = JSON.parse(input);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return String(parsed[0]);
      }
      return String(parsed);
    }
  } catch (error) {
    return String(input);
  }
};

export function getErpStatus(status: string) {
  return extractFilterValue(status) === 'undefined' ? status : extractFilterValue(status);
}

export function checkIfExpired(status: ErpStatus) {
  return (
    [ERP_STATUSES.DISCONTINUED, ERP_STATUSES.EXPIRED].includes(status)
  );
}
